import React, {useEffect, useRef, useState} from "react";
import BaseModal from "../../modals/base-modal";
import useLocationStore from "../config/use-location-store";
import {instanceMob} from "../../../utils/utils";
import InlineModal from "../../info-modal/infoModal";
import {getBackendError} from "../../../utils/error-helpers";
import {Button} from "react-bootstrap";
import FormField from "../../forms/form-field";
import Tooltip from "../../tooltip/tooltip";
import FormSelect from "../../forms/form-select";
import "./styles.css";

const STATUS_OPEN = 10;
const STATUS_PRE_CLOSE = 20;
const STATUS_CLOSED = 30;

const CashboxModal = ({show, onClose}) => {
    const formRef = useRef(null);
    const hasFetched = useRef(false);
    const [infoModal, setInfoModal] = useState(null);
    const [userData, setUserData] = useState(null);
    const [cashbox, setCashbox] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [confirmCloseModal, setConfirmCloseModal] = useState(false);
    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const openingFields = [
        {
            Component: FormField,
            label: "Fecha",
            name: "date",
            type: "text",
            value: cashbox?.date
                ? new Date(cashbox.date.replace(" ", "T")).toLocaleString()
                : new Date().toLocaleString(),
            readOnly: true,
        },
        {
            Component: FormField,
            label: "Responsable",
            name: "responsible",
            type: "text",
            value:
                cashbox?.responsible_open ||
                (userData
                    ? `${userData.first_name} ${userData.last_name} (${userData.email})`
                    : "Usuario predeterminado"),
            readOnly: true,
            tooltip:
                "Persona asignada para recibir, custodiar y cerrar la caja. Este será el encargado oficial del manejo del dinero durante el turno o jornada.",
        },
        {
            Component: FormField,
            label: "Locación",
            name: "location_display",
            type: "text",
            value: selectedLocation?.name || "Locación predeterminada",
            readOnly: true,
        },
        {
            Component: FormSelect,
            label: "Línea de negocio",
            name: "box_type",
            placeholder: "Selecciona línea de negocio",
            value: cashbox?.box_type || "",
            options:
                cashbox?.available_type === 10
                    ? [{value: 10, label: "Gimnasio (Planes y Productos)"}]
                    : cashbox?.available_type === 20
                        ? [{value: 20, label: "Tienda (Productos)"}]
                        : [
                            {value: 10, label: "Gimnasio (Planes y Productos)"},
                            {value: 20, label: "Tienda (Productos)"},
                        ],
            disabled: isLoading,
            tooltip:
                "Selecciona la línea de negocio asociada a esta caja. Esto permite clasificar correctamente las operaciones y reportes contables según el tipo de actividad.",
            required: true,
        },
        {
            Component: FormField,
            label: "Efectivo apertura",
            name: "opening_cash",
            value:
                cashbox?.opening_cash !== undefined
                    ? cashbox.opening_cash
                    : null,
            type: "currency",
            placeholder: "Escribe aquí",
            required: true,
            tooltip:
                "Monto inicial en efectivo con el que se comienza la jornada o turno. Este valor servirá como base para las operaciones de caja del día.",
        },
    ];

    const closingFields = [
        {
            Component: FormField,
            label: "Responsable",
            name: "responsible_close",
            type: "text",
            value: userData ? `${userData.first_name} ${userData.last_name} (${userData.email})` : "Usuario predeterminado",
            readOnly: true,
        },
        {
            Component: FormField,
            label: "Ingresos",
            name: "incomes_total",
            type: "currency",
            value: cashbox?.incomes_total || "0",
            readOnly: true,
            tooltip: "Suma total de dinero recibido durante la jornada o turno, proveniente de ventas, abonos o cualquier otro concepto de entrada de efectivo."
        },
        {
            Component: FormField,
            label: "Egresos",
            name: "outcomes_total",
            type: "currency",
            value: cashbox?.outcomes_total || "0",
            readOnly: true,
            tooltip: "Total de salidas de dinero durante la jornada o turno, incluyendo pagos a proveedores, compras, gastos operativos u otros desembolsos realizados desde la caja."
        },
        {
            Component: FormField,
            label: "Efectivo cierre",
            name: "closing_cash",
            type: "currency",
            value: cashbox?.closing_cash_total || "0",
            readOnly: true,
            tooltip: "Monto total de dinero en efectivo que queda en caja al finalizar la jornada o turno, después de sumar ingresos y restar egresos."
        },
        {
            Component: FormField,
            label: "Validador",
            name: "validator",
            type: "text",
            placeholder: "Escribe aquí",
            tooltip: "Persona encargada de revisar y confirmar que los valores registrados en la apertura y cierre de caja sean correctos, validando la información antes de su registro final."
        },
    ];

    useEffect(() => {
        const storedUser = {
            email: localStorage.getItem("email"),
            first_name: localStorage.getItem("first_name"),
            last_name: localStorage.getItem("last_name"),
            id: localStorage.getItem("id"),
        };
        setUserData(storedUser);
    }, []);

    const fetchCashbox = () => {
        setIsLoading(true);
        instanceMob
            .get("admin/accounting/cashbox", {
                params: {location_id: selectedLocation?.id},
            })
            .then((res) => {
                const data = res.data;

                if (data?.warning && !infoModal) {
                    setInfoModal({
                        iconType: "warning",
                        title: "Advertencia",
                        description: data.warning,
                        primaryAction: {
                            label: "Aceptar",
                            onClick: () => setInfoModal(null),
                        },
                    });
                }

                if (data?.available_type && Object.keys(data).length === 1) {
                    setCashbox({
                        available_type: data.available_type,
                        box_type: data.available_type,
                        isOpenable: true,
                        isClosable: false,
                    });
                    return;
                }

                if (data && Object.keys(data).length > 0) {
                    if (data.available_type && !data.box_type) {
                        data.box_type = data.available_type;
                    }
                    setCashbox({
                        ...data,
                        isOpenable: false,
                        isClosable: true,
                    });
                } else {
                    setCashbox(null);
                }

            })
            .catch((err) => {
                const errorMessage = getBackendError(err);
                setInfoModal({
                    iconType: "error",
                    title: "Error",
                    description: errorMessage,
                    secondaryAction: {
                        label: "Cerrar",
                        onClick: () => setInfoModal(null),
                    },
                });
            })
            .finally(() => setIsLoading(false));
    };

    const handleSubmit = (status) => {
        setIsLoading(true);

        const formData = new FormData(formRef.current);
        const payload = Object.fromEntries(formData.entries());

        const finalPayload = {
            ...cashbox,
            ...payload,
            status,
            location_id: selectedLocation?.id,
        };

        ["opening_cash", "incomes_total", "outcomes_total", "closing_cash"].forEach(
            (field) => {
                if (finalPayload[field] !== undefined) {
                    finalPayload[field] = Number(finalPayload[field]) || 0;
                }
            }
        );

        instanceMob
            .post("admin/accounting/cashbox/create", finalPayload)
            .then(() => {
                let title = "";
                let description = "";

                if (status === STATUS_OPEN) {
                    title = "Caja abierta con éxito";
                    description = "El monto inicial de apertura quedó registrado correctamente.";
                } else if (status === STATUS_CLOSED) {
                    title = "Caja cerrada con éxito";
                    description = "El cierre de caja se completó y los datos se guardaron.";
                } else if (status === STATUS_PRE_CLOSE) {
                    title = "Caja en pre-cierre";
                    description = "La caja ha sido marcada como pre-cerrada. Puedes revisar los datos antes del cierre definitivo.";
                }

                setInfoModal({
                    iconType: "success",
                    title,
                    description,
                    primaryAction: {
                        label: "Aceptar",
                        onClick: () => {
                            setInfoModal(null);
                            onClose();
                        },
                    },
                });
            })
            .catch((err) => {
                const errorMessage = getBackendError(err);
                setInfoModal({
                    iconType: "error",
                    title: "Error",
                    description: errorMessage,
                    secondaryAction: {
                        label: "Cerrar",
                        onClick: () => setInfoModal(null),
                    },
                });
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (!hasFetched.current) {
            fetchCashbox();
            hasFetched.current = true;
        }
    }, [selectedLocation.id]);

    return (
        <>
            <BaseModal
                show={show}
                onClose={onClose}
                title="Apertura y Cierre de Caja"
                size={"lg"}
            >
                <form ref={formRef} className="d-flex flex-column gap-2" onSubmit={(e) => e.preventDefault()}>

                    {openingFields.map((field, index) => {
                        const FieldComponent = field.Component;
                        const inputElement = (
                            <FieldComponent key={index} id={field.name} {...field}
                                            disabled={isLoading || (!!cashbox && !cashbox?.isOpenable)}/>
                        );

                        return field.tooltip ? (
                            <Tooltip
                                key={index}
                                title={field.label}
                                description={field.tooltip}
                                position="right"
                                variant="rich"
                            >
                                {inputElement}
                            </Tooltip>
                        ) : inputElement;
                    })}

                    <div className="d-flex align-self-center mt-2">
                        <Button
                            className="base-modal-done-button"
                            variant="primary"
                            disabled={isLoading || (!!cashbox && !cashbox?.isOpenable)}
                            onClick={() => handleSubmit(STATUS_OPEN)}
                        >
                            Apertura
                        </Button>
                    </div>

                    {cashbox?.isClosable && !isLoading && cashbox?.status !== STATUS_PRE_CLOSE && cashbox?.status !== STATUS_CLOSED && (
                        <div className="d-flex flex-column p-3 pb-0">
                            <span className="cash-box-modal-preclosing-label mb-2">
                                Por favor, verifica los valores del pre-cierre antes de continuar.
                                Si estás de acuerdo con los montos registrados, podrás proceder con el cierre definitivo.
                            </span>
                            <span className="cash-box-modal-preclosing-label">
                                En caso de que no estés conforme con los valores, puedes dejar un comentario explicando la observación para que el área administrativa pueda revisarlo.
                            </span>
                        </div>
                    )}

                    {closingFields.map((field, index) => {
                        const FieldComponent = field.Component;
                        const inputElement = (
                            <FieldComponent
                                key={`closing-${index}`}
                                id={field.name}
                                {...field}
                                disabled={isLoading || (!cashbox || !cashbox?.isClosable)}
                            />
                        );

                        return field.tooltip ? (
                            <Tooltip
                                key={`closing-${index}`}
                                title={field.label}
                                description={field.tooltip}
                                position="right"
                                variant="rich"
                            >
                                {inputElement}
                            </Tooltip>
                        ) : inputElement;
                    })}

                    {cashbox?.isClosable && !isLoading && cashbox?.status !== STATUS_PRE_CLOSE && cashbox?.status !== STATUS_CLOSED && (
                        <div className="d-flex flex-column p-3 pb-0 gap-2">
                            <span className="cash-box-modal-preclosing-label mb-2">
                                ¿Estás de acuerdo con los valores del pre-cierre?
                            </span>

                            <div className="d-flex flex-row justify-content-center gap-4 mb-2">
                                <div className="d-flex flex-row justify-content-center align-items-center">
                                    <input
                                        className="cash-box-modal form-check-input mt-0 me-2"
                                        type="checkbox"
                                        checked={!cashbox.in_disagreement}
                                        onChange={() =>
                                            setCashbox((prev) => ({ ...prev, in_disagreement: false }))
                                        }
                                    />
                                    <span className="cash-box-modal-preclosing-label">
                                        Sí, estoy de acuerdo
                                    </span>
                                </div>

                                <div className="d-flex flex-row justify-content-center align-items-center">
                                    <input
                                        className="cash-box-modal form-check-input mt-0 me-2"
                                        type="checkbox"
                                        checked={cashbox.in_disagreement || false}
                                        onChange={() =>
                                            setCashbox((prev) => ({ ...prev, in_disagreement: true }))
                                        }
                                    />
                                    <span className="cash-box-modal-preclosing-label">
                                        No, dejar comentario
                                    </span>
                                </div>
                            </div>

                            <FormField
                                type={cashbox.in_disagreement ? "textarea" : "hidden"}
                                placeholder="Escribe aquí"
                                name="notes"
                                value={cashbox.notes || ""}
                                onChange={(e) =>
                                    setCashbox((prev) => ({ ...prev, notes: e.target.value }))
                                }
                                disabled={!cashbox.in_disagreement}
                                required={cashbox.in_disagreement}
                            />
                        </div>
                    )}

                    <input type="hidden" name="location_id" value={selectedLocation?.id || ""}/>
                </form>

                <BaseModal.Footer>
                    <Button
                        className="base-modal-cancel-button"
                        variant="secondary"
                        onClick={onClose}
                    >
                        Cancelar
                    </Button>
                    <Button
                        className="base-modal-done-button"
                        variant="primary"
                        onClick={() => handleSubmit(STATUS_PRE_CLOSE)}
                        disabled={
                            !cashbox?.isClosable ||
                            isLoading ||
                            cashbox?.status === STATUS_PRE_CLOSE ||
                            cashbox?.status === STATUS_CLOSED
                        }
                    >
                        Pre - Cierre
                    </Button>
                    <Button
                        className="base-modal-done-button"
                        variant="primary"
                        onClick={() => setConfirmCloseModal(true)}
                        disabled={
                            !cashbox?.isClosable ||
                            isLoading ||
                            cashbox?.status === STATUS_OPEN ||
                            cashbox?.status === STATUS_CLOSED
                        }
                    >
                        Cierre
                    </Button>
                </BaseModal.Footer>
            </BaseModal>

            {infoModal && (
                <InlineModal
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}

            {confirmCloseModal && (
                <InlineModal
                    title="Cierre de Caja del Día"
                    description={`Estás por realizar el cierre definitivo de la caja del día. Una vez confirmes esta acción, no podrás registrar nuevas ventas, gastos o movimientos correspondientes a esta fecha, hasta que se realice una nueva apertura de caja. Por favor, verifica que toda la información esté completa y correcta antes de continuar. El cierre de caja es un proceso que consolida los ingresos y egresos del día, dejando un registro final para control y seguimiento contable.`}
                    primaryAction={{
                        label: "Confirmar cierre definitivo",
                        onClick: () => {
                            setConfirmCloseModal(false);
                            handleSubmit(STATUS_CLOSED);
                        }
                    }}
                    secondaryAction={{
                        label: "Revisar antes de cerrar",
                        onClick: () => setConfirmCloseModal(false)
                    }}
                />
            )}

        </>
    );
};

export default CashboxModal;
